import React, { useMemo } from 'react';

interface TrendSparklineProps {
  dataPoints: { date: string; interest: number }[];
}

export interface TrendInfo {
  color: string;
  label: string;
}

export function computeTrendInfo(dataPoints: { date: string; interest: number }[]): TrendInfo | null {
  if (!dataPoints || dataPoints.length < 2) return null;
  if (dataPoints.every(d => d.interest === 0)) return null;

  const sorted = [...dataPoints].sort((a, b) => a.date.localeCompare(b.date));
  const now = new Date();
  const twelveMonthsAgo = new Date(now.getFullYear() - 1, now.getMonth(), 1);
  const twentyFourMonthsAgo = new Date(now.getFullYear() - 2, now.getMonth(), 1);

  const recent = sorted.filter(d => new Date(d.date) >= twelveMonthsAgo);
  const prior = sorted.filter(d => {
    const dt = new Date(d.date);
    return dt >= twentyFourMonthsAgo && dt < twelveMonthsAgo;
  });

  const avgRecent = recent.length > 0 ? recent.reduce((s, d) => s + d.interest, 0) / recent.length : 0;
  const avgPrior = prior.length > 0 ? prior.reduce((s, d) => s + d.interest, 0) / prior.length : 0;

  if (avgPrior === 0) return { color: '#ff9800', label: 'Flat trend' };

  const growth = (avgRecent - avgPrior) / avgPrior;
  if (growth > 0.05) return { color: '#4caf50', label: `+${(growth * 100).toFixed(0)}%` };
  if (growth < -0.05) return { color: '#f44336', label: `${(growth * 100).toFixed(0)}%` };
  return { color: '#ff9800', label: 'Flat' };
}

const WIDTH = 80;
const HEIGHT = 24;
const PADDING = 2;

const TrendSparkline: React.FC<TrendSparklineProps> = ({ dataPoints }) => {
  const { points, color } = useMemo(() => {
    if (!dataPoints || dataPoints.length < 2) return { points: '', color: '' };
    if (dataPoints.every(d => d.interest === 0)) return { points: '', color: '' };

    const sorted = [...dataPoints].sort((a, b) => a.date.localeCompare(b.date));

    const maxInterest = Math.max(...sorted.map(d => d.interest));
    const minInterest = Math.min(...sorted.map(d => d.interest));
    const range = maxInterest - minInterest || 1;

    const drawW = WIDTH - PADDING * 2;
    const drawH = HEIGHT - PADDING * 2;

    const pts = sorted
      .map((d, i) => {
        const x = PADDING + (i / (sorted.length - 1)) * drawW;
        const y = PADDING + drawH - ((d.interest - minInterest) / range) * drawH;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');

    const info = computeTrendInfo(dataPoints);
    return { points: pts, color: info?.color ?? '#ff9800' };
  }, [dataPoints]);

  if (!points) return null;

  return (
    <svg width={WIDTH} height={HEIGHT} viewBox={`0 0 ${WIDTH} ${HEIGHT}`} style={{ display: 'block' }}>
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export default TrendSparkline;
